import { Suspense, lazy, type ReactNode } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import { Analytics } from './components/Analytics';
import { AppShell } from './components/AppShell';
import { Seo } from './components/Seo';

const HomePage = lazy(() => import('./pages/HomePage').then((module) => ({ default: module.HomePage })));
const ImageResizerPage = lazy(() => import('./pages/ImageResizerPage').then((module) => ({ default: module.ImageResizerPage })));
const ImageCompressorPage = lazy(() => import('./pages/ImageCompressorPage').then((module) => ({ default: module.ImageCompressorPage })));
const WebpToJpgPage = lazy(() => import('./pages/WebpToJpgPage').then((module) => ({ default: module.WebpToJpgPage })));
const JpgToPdfPage = lazy(() => import('./pages/JpgToPdfPage').then((module) => ({ default: module.JpgToPdfPage })));
const MergePdfPage = lazy(() => import('./pages/MergePdfPage').then((module) => ({ default: module.MergePdfPage })));
const SplitPdfPage = lazy(() => import('./pages/SplitPdfPage').then((module) => ({ default: module.SplitPdfPage })));
const ImageFormatConverterPage = lazy(() =>
  import('./pages/ImageFormatConverterPage').then((module) => ({ default: module.ImageFormatConverterPage }))
);
const RotatePdfPage = lazy(() => import('./pages/RotatePdfPage').then((module) => ({ default: module.RotatePdfPage })));
const TrimPdfPage = lazy(() => import('./pages/TrimPdfPage').then((module) => ({ default: module.TrimPdfPage })));

function InfoPage({
  title,
  summary,
  seoTitle,
  seoDescription,
  path,
  children,
}: {
  title: string;
  summary: string;
  seoTitle: string;
  seoDescription: string;
  path: string;
  children: ReactNode;
}) {
  return (
    <div className="static-page">
      <Seo title={seoTitle} description={seoDescription} path={path} />
      <header className="static-hero">
        <span className="home-eyebrow">PrivateFileKit</span>
        <h1>{title}</h1>
        <p>{summary}</p>
      </header>
      <article className="static-card">{children}</article>
    </div>
  );
}

function AboutPage() {
  return (
    <InfoPage
      title="About"
      summary="PrivateFileKit is built for practical image and PDF tasks that should stay in your browser."
      seoTitle="About PrivateFileKit | Private Image and PDF Tools"
      seoDescription="Learn how PrivateFileKit helps you resize, convert, compress, and edit files locally in your browser without uploads."
      path="/about"
    >
      <h2>Built for everyday file work</h2>
      <p>
        PrivateFileKit focuses on the file tasks people need most often: resizing images, converting formats, combining PDFs, removing pages,
        and preparing files for sharing. The goal is a fast, dependable toolkit that works without accounts, complicated setup, or server-side
        processing.
      </p>
      <h2>Private by design</h2>
      <p>
        Supported file processing happens locally in your browser whenever possible. That means your images and PDFs stay on your device while
        the tool runs, which is especially useful for personal documents, internal drafts, and anything you do not want to upload to a third
        party service.
      </p>
      <h2>Clean tools, not clutter</h2>
      <p>
        Each tool is designed to be straightforward on both desktop and mobile. We aim to keep the interface focused, readable, and efficient
        so you can finish the task quickly and move on.
      </p>
      <h2>Current scope</h2>
      <p>
        PrivateFileKit currently offers a focused set of browser-based image and PDF utilities. We add features carefully, with an emphasis on
        privacy, usability, and predictable local processing rather than feature sprawl.
      </p>
    </InfoPage>
  );
}

function PrivacyPage() {
  return (
    <InfoPage
      title="Privacy"
      summary="This page explains how PrivateFileKit handles files, technical data, and future privacy updates."
      seoTitle="Privacy Policy | PrivateFileKit"
      seoDescription="Read the PrivateFileKit privacy policy, including how local file processing works and what limited technical data may be handled."
      path="/privacy"
    >
      <p className="static-meta">Last updated: June 24, 2026</p>
      <h2>Your files stay local</h2>
      <p>
        PrivateFileKit is designed so supported file processing happens locally in your browser. When you resize an image, merge a PDF, or use
        another supported tool, the file data is handled on your device rather than uploaded to PrivateFileKit servers.
      </p>
      <h2>What we do not collect</h2>
      <p>
        We do not intentionally collect or store the contents of the files you process through the browser-based tools on this site. We also do
        not require an account to use the current tools.
      </p>
      <h2>Technical website data</h2>
      <p>
        Like most websites, basic technical data may be processed by hosting, security, and delivery providers. This can include information such
        as IP address, browser type, request timing, and diagnostic logs needed to keep the service available and secure.
      </p>
      <h2>Cookies and analytics</h2>
      <p>
        PrivateFileKit does not currently rely on heavy tracking for its file tools. If analytics, consent tools, or similar services are added
        later, this page will be updated to explain what is collected and how it is used.
      </p>
      <h2>Third-party dependencies</h2>
      <p>
        The application uses client-side libraries to process supported image and PDF formats directly in the browser. Those libraries help run
        the tool features but do not change our goal of keeping supported processing local.
      </p>
      <h2>Policy updates</h2>
      <p>
        We may update this Privacy Policy as the service evolves. When we do, we will revise the date above so you can see when the latest
        changes were made.
      </p>
    </InfoPage>
  );
}

function TermsPage() {
  return (
    <InfoPage
      title="Terms"
      summary="These terms cover acceptable use, service scope, and your responsibilities when using PrivateFileKit."
      seoTitle="Terms of Use | PrivateFileKit"
      seoDescription="Read the PrivateFileKit terms of use for browser-based image and PDF tools, including service limits and user responsibilities."
      path="/terms"
    >
      <p className="static-meta">Last updated: June 24, 2026</p>
      <h2>Using the tools</h2>
      <p>
        You may use PrivateFileKit for lawful file-processing tasks and only with files that you own or are authorized to process. You are
        responsible for how you use the site and for reviewing output files before sharing or publishing them.
      </p>
      <h2>No guarantee for every workflow</h2>
      <p>
        We aim to provide reliable browser-based tools, but we cannot guarantee that every file, browser, or device configuration will work
        perfectly in every case. Compatibility can vary depending on format complexity, file size, browser support, and local device limits.
      </p>
      <h2>Keep original copies</h2>
      <p>
        Always keep backup copies of important files. You should verify downloaded results before replacing, sending, or relying on them for
        personal, business, or legal purposes.
      </p>
      <h2>Service changes</h2>
      <p>
        Features, supported formats, limits, and design details may change over time as PrivateFileKit develops. We may add, modify, or remove
        functionality to improve quality, privacy, or maintainability.
      </p>
      <h2>Availability</h2>
      <p>
        The service is provided on an as-is basis. We do our best to keep it available, but uptime, speed, and compatibility are not guaranteed.
      </p>
      <h2>Questions</h2>
      <p>
        If public contact details are added to the site in the future, this page may be updated with a support channel for general questions
        about the service.
      </p>
    </InfoPage>
  );
}

function NotFoundPage() {
  return (
    <div className="not-found-page">
      <Seo
        title="Page Not Found | PrivateFileKit"
        description="The page you requested could not be found on PrivateFileKit."
        path="/404"
      />
      <div className="not-found-card">
        <span className="home-eyebrow">404</span>
        <h1>Page not found</h1>
        <p>The page you requested does not exist or may have moved. You can head back to the homepage to choose a tool.</p>
        <Link className="button primary" to="/">
          Back to homepage
        </Link>
      </div>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="route-loading" role="status" aria-live="polite">
      <div className="route-loading-card">
        <span className="home-eyebrow">Loading</span>
        <p>Preparing the tool...</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <>
      <Analytics />
      <AppShell>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/image-resizer" element={<ImageResizerPage />} />
            <Route path="/image-compressor" element={<ImageCompressorPage />} />
            <Route path="/webp-to-jpg" element={<WebpToJpgPage />} />
            <Route path="/png-to-jpg" element={<ImageFormatConverterPage from="PNG" to="JPG" />} />
            <Route path="/jpg-to-png" element={<ImageFormatConverterPage from="JPG" to="PNG" />} />
            <Route path="/jpg-to-pdf" element={<JpgToPdfPage />} />
            <Route path="/merge-pdf" element={<MergePdfPage />} />
            <Route path="/split-pdf" element={<SplitPdfPage />} />
            <Route path="/rotate-pdf" element={<RotatePdfPage />} />
            <Route path="/remove-pdf-pages" element={<TrimPdfPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AppShell>
    </>
  );
}
